import stableStringify from 'json-stable-stringify';
import isPlainObject from 'lodash/isPlainObject';
import isEmpty from 'lodash/isEmpty';

/**
 * WIP
 * Base global store implementation that abstracts away api/caching/rehydration
 * for resources manipulated via API and UI.
 * @example
 * // will call callback every time data is made available (via cache, network,
 * // or setting explicitly in front-end code)
 * foobarStore.subscribe(foobar => doSomethingWith(foobar));
 */
import {getOwnAttr} from 'common/util';
import {purgeFetchCache} from 'common/report/queries/utils/fetch-cache';
import {removeByValue} from 'common/util/array';
import {ApiResponseError} from 'common/util/api-client';

import {reporter} from '../error-reporting';
import {BaseClient, QueryOptions} from '../queries/utils/base-client';
import {deleteFromFetchCache} from '../queries/utils/fetch-cache';

type TransformType<Value> = {
  /**
   * transform GET API response into this.value
   */
  index?: (result: any, {store, subscription}: {store?: Store<Value>; subscription?: Subscription}) => void;
  /**
   * transform POST/PUT API response into a {k, v} map. v will be returned out of POST/PUT
   * and the store will patch {[k], v} in to this.value
   */
  update?: (result: any, {store, subscription}: {store?: Store<Value>; subscription?: Subscription}) => void;
};

export interface StoreOptions<Value> {
  apiClient: BaseClient;
  defaultValue: Partial<Value>;
  name?: string;
  transform?: TransformType<Value>;
}

export interface Subscription {
  key: string;

  params: Obj;
  options: Obj;

  initialized: boolean;
  callbacks: SubscriptionCallback[];
}

export interface SubscriptionCallbackParams {
  cached?: boolean;
  error?: boolean;
  fromUpdate?: boolean;
}

export type SubscriptionCallback = (args: SubscriptionCallbackParams) => void;

/**
 * Factory for creating a BaseClient from a given URL to facilitate
 * easy Store usage until apiClient is migrated to use ApiClient directly
 *
 * @param urlFn a function that generates the url from params
 */
export function createSimpleClient(urlFn: (params: any) => string): BaseClient {
  return new (class extends BaseClient {
    getUrl(params) {
      return urlFn(params);
    }
    processResponse(response) {
      return response.results;
    }
  })();
}

export class Store<Value> {
  static getKey(params = {}, options = {}) {
    return stableStringify({params, options});
  }

  public value: Value;

  private apiClient = null;
  private transform: TransformType<Value> = {};
  private name: string = null;
  private options: StoreOptions<Value>;

  private subscriptions = new Map<string, Subscription>();

  constructor(options: StoreOptions<Value>) {
    this.options = options;
    this.apiClient = this.options.apiClient;
    this.name = this.options.name;

    // TODO: remove this cast, defaultValue is only Partial<Value>
    this.value = this.options.defaultValue as Value;

    if (this.options.transform) {
      this.transform = this.options.transform;
    }
  }

  async fetch(params: {[key: string]: any} = {}, options = {}, key = ``) {
    const startTime = new Date().getTime();
    let networkFaster = false,
      initialized = false;
    const subscription = this.getSubscription(key);

    if (subscription.initialized) {
      // give an update if we have initialized
      this.fireCallbacks(key);
      initialized = true;
    } else {
      subscription.params = params;
      subscription.options = options;

      const networkPromise = this.queryForSubscription(subscription, {updateFetchCache: true})
        .then((result: any) => {
          this.processQueryResult(result, subscription);
          networkFaster = true;
        })
        .catch((err: Error) => this.processQueryError(err, subscription));

      const fetchCachePromise = this.queryForSubscription(subscription, {fromFetchCache: true})
        .then((result: any) => {
          // only update store if fetch cache returns before network
          if (!networkFaster) {
            this.processQueryResult(result, subscription);
          }
        })
        .catch((err: Error) => this.processQueryError(err, subscription));

      // race both network and fetchCache, fall back to network if there was a miss on fetchCache
      await Promise.race([fetchCachePromise, networkPromise]);
      if (!subscription.initialized) {
        await networkPromise;
      }
    }

    mixpanel.infra_metrics?.track(`store-fetch`, {
      endpoint: this.apiClient.buildEndpoint(params),
      initialized,
      latency_ms: new Date().getTime() - startTime,
      name: this.name,
      network_faster: networkFaster,
      host: window.location.host,
      project_id: params.projectId,
      workspace_id: params.workspaceId,
    });
  }

  async refresh(params = {}, options = {}, key = ``) {
    const subscription = this.getSubscription(key);

    if (!subscription.initialized) {
      subscription.params = params;
      subscription.options = options;
    }

    await this.fetchForSubscription(subscription, {clearCache: true, updateFetchCache: true}, false);
  }

  subscribe(callback, key = ``) {
    this.getSubscription(key).callbacks.push(callback);
    return this; // chainable
  }

  unsubscribe(callback, keys = ``) {
    const subscription = this.subscriptions.get(keys);
    if (subscription.callbacks.includes(callback)) {
      subscription.callbacks = removeByValue(subscription.callbacks, callback);
    }
  }

  fireCallbacks(key = ``, meta: SubscriptionCallbackParams = {}) {
    for (const callback of this.getSubscription(key).callbacks) {
      callback(meta);
    }
  }

  hasFetchedValue(key = ``) {
    return this.getSubscription(key).initialized;
  }

  async post(params, key = ``) {
    const result = await this.write(params, {method: `POST`}, key);
    this.invalidateFetchCache();
    return result;
  }

  async put(itemKey, params, key = ``) {
    const result = await this.write(params, {method: `PUT`}, key, itemKey);
    this.invalidateFetchCache();
    return result;
  }

  async patch(params, key = ``) {
    const result = await this.write(params, {method: `PATCH`}, key);
    this.invalidateFetchCache();
    return result;
  }

  async delete(itemKey, params, key = ``) {
    await this.apiClient.query(params, {clearCache: true, method: `DELETE`});
    delete this.value[itemKey];
    this.invalidateFetchCache();

    this.fireCallbacks(key);
  }

  /**
   * Performs a partial update for object like updates and a value
   * replacement for other type of updates
   *
   * @param valueUpdate update to patch or replace the store value
   */
  _update(valueUpdate: Partial<Value> = {}) {
    if (Array.isArray(this.value)) {
      if (!Array.isArray(valueUpdate)) {
        throw new Error(`valueUpdate isn't an array but the store is`);
      }
      // the 'as unknown' is to work about the following:
      // TS2352: Conversion of type 'any[]' to type 'Value' may be a mistake because
      // neither type sufficiently overlaps with the other. If this was intentional,
      // convert the expression to 'unknown' first.
      this.value = (valueUpdate as unknown) as Value;
    } else if (isPlainObject(valueUpdate)) {
      // if it's an object, we support partial update
      this.value = {...this.value, ...valueUpdate};
    } else {
      this.value = valueUpdate as Value;
    }
  }

  update(valueUpdate: Partial<Value>, key = ``) {
    if (Array.isArray(valueUpdate) || Object.keys(valueUpdate).length > 0) {
      this._update(valueUpdate);
      this.fireCallbacks(key, {fromUpdate: true});
    }
  }

  async refreshAll() {
    // All sucscribers may not have attached themselves yet, so we purge the fetch cache so that they will get the latest values
    // when they do try, for exsisting subscriptions we can make sure they have the latest values cached.
    await purgeFetchCache({ttl: 0, path: `/${this.apiClient.buildEndpoint()}`});
    await Promise.allSettled(
      Array.from(this.subscriptions.values()).map((subscription) =>
        this.fetchForSubscription(subscription, {clearCache: true, updateFetchCache: true}, false),
      ),
    );
  }

  // useful for testing
  // it pierces 2 layers of caching which are both embedded within import-singletons (store and baseclient singletons) which persist across WCT tests
  public async reset(): Promise<void> {
    await this.invalidateFetchCache();
    this.subscriptions = new Map();
    // TODO: remove this cast, defaultValue is only Partial<Value>
    this.value = this.options.defaultValue as Value;
    this.apiClient.responseCache.clearAll();
  }

  public getSubscription(key) {
    let subscription = this.subscriptions.get(key);
    if (!subscription) {
      subscription = {
        key,
        params: null,
        options: null,
        initialized: false,
        callbacks: [],
      };
    }
    this.subscriptions.set(key, subscription);
    return subscription;
  }

  // Safe access a property on the store value, with an optional default there's no fetched value
  public get<K extends keyof NonNullable<Value>, V extends NonNullable<Value>[K]>(
    key: K,
    {defaultValue}: {defaultValue?: V},
  ): V | null {
    return this.hasFetchedValue() ? getOwnAttr(this.value, String(key)) : defaultValue ?? null;
  }

  private async invalidateFetchCache(): Promise<void> {
    for (const subscription of this.subscriptions.values()) {
      if (subscription.initialized && subscription.params && subscription.options) {
        await deleteFromFetchCache(
          this.apiClient.getUrl(subscription.params),
          this.apiClient.getFetchOptions(subscription.params, subscription.options),
        );
      }
    }
  }

  private queryForSubscription(subscription: Subscription, extraOptions: QueryOptions, useQueryCache = null) {
    useQueryCache = useQueryCache !== null ? {useQueryCache} : {};
    return this.apiClient.query({...subscription.params, ...useQueryCache}, {...subscription.options, ...extraOptions});
  }

  private processQueryResult(result: any, subscription: Subscription) {
    if (result) {
      this._update(this.transform.index ? this.transform.index(result, {subscription, store: this}) : result);
      subscription.initialized = true;
      this.fireCallbacks(subscription.key, {cached: true});
    }
  }

  private processQueryError(err: Error, subscription: Subscription) {
    this.fireCallbacks(subscription.key, {error: true});

    reporter.error(err);
  }

  // TODO: consolidate `fetchForSubscription` and `write`. so far there is some divergence
  // - `fetchForSubscription` is catching and reporting errors to rollbar, whereas clients of `post` and
  // `put` expect and need error information to display on the UI
  // - the `index` and the `update` transform functions have different return formats
  // - `fetchForSubscription` sets this.initialized
  private async fetchForSubscription(subscription: Subscription, extraOptions: QueryOptions, useQueryCache = null) {
    const startTime = new Date().getTime();
    let error = false,
      errorMessage: string;

    try {
      const result = await this.queryForSubscription(subscription, extraOptions, useQueryCache);
      this.processQueryResult(result, subscription);
    } catch (err) {
      this.processQueryError(err, subscription);

      error = true;
      errorMessage = err.message;
    }

    mixpanel.infra_metrics?.track(`store-fetch-for-subscription`, {
      endpoint: this.apiClient.buildEndpoint(subscription.params),
      error,
      error_message: errorMessage,
      latency_ms: new Date().getTime() - startTime,
      name: this.name,
      host: window.location.host,
      project_id: subscription.params.projectId,
      workspace_id: subscription.params.workspaceId,
      update_fetch_cache: extraOptions.updateFetchCache,
      clear_cache: extraOptions.clearCache,
      from_fetch_cache: extraOptions.fromFetchCache,
      use_query_cache: isEmpty(useQueryCache) ? true : useQueryCache?.useQueryCache,
    });
  }

  private async write(params, options: QueryOptions, key = ``, itemKey = null) {
    const result = await this.apiClient.query(params, options);
    if (result.status === `error`) {
      throw new ApiResponseError(result, result.error);
    }

    // 'itemKey' isn't technically necessary as client of `put` knows the itemKey and puts it into
    // `params`, but we add it to the `put` interface to mirror the `delete` interface
    const {k, v} = await (this.transform.update ? this.transform.update(result, itemKey) : result);
    this._update({[k]: v} as Value);
    this.fireCallbacks(key);
    return v;
  }

  // TODO async write (to api), invalidate
}
